import React, { useState, useEffect, useRef } from 'react';
import { Ticket, Message, TicketStatus, TicketSite, TicketCategory } from '../../../entities/ticket';
import { getTicketCategories, createYouTrackIssue } from '../../../entities/ticket/api/ticketApi';
import { useTheme } from '../../../shared/lib/themeContext';
import { SendMessageUseCase, GetTicketMessagesUseCase, UpdateTicketStatusUseCase, UpdateTicketUseCase } from '../application';
import { CategoriesModal } from './CategoriesModal';
import { TemplatesModal } from './TemplatesModal';
import { StatusModal } from './StatusModal';
import { SiteModal } from './SiteModal';
import { S3Icon } from '../../../shared/ui';
import * as S from './TicketChat.styles';

interface TicketChatProps {
  ticket: Ticket | null;
  onMessageSent?: () => void;
  onStatusChange?: (newStatus?: TicketStatus) => void;
}

export const TicketChat: React.FC<TicketChatProps> = ({ ticket, onMessageSent, onStatusChange }) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [sending, setSending] = useState(false);
  const [currentStatus, setCurrentStatus] = useState<TicketStatus | null>(null);
  const [currentSite, setCurrentSite] = useState<TicketSite | null>(null);
  const [currentCategory, setCurrentCategory] = useState<string>('');
  const [categories, setCategories] = useState<TicketCategory[]>([]);
  const [isCategoriesModalOpen, setIsCategoriesModalOpen] = useState(false);
  const [isTemplatesModalOpen, setIsTemplatesModalOpen] = useState(false);
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [isSiteModalOpen, setIsSiteModalOpen] = useState(false);
  const [attachments, setAttachments] = useState<File[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { mode } = useTheme();

  const sendMessageUseCase = new SendMessageUseCase();
  const getTicketMessagesUseCase = new GetTicketMessagesUseCase();
  const updateTicketStatusUseCase = new UpdateTicketStatusUseCase();
  const updateTicketUseCase = new UpdateTicketUseCase();

  useEffect(() => {
    if (ticket) {
      setCurrentStatus(ticket.status);
      setCurrentSite(ticket.site);
      setCurrentCategory(ticket.category || '');
      loadMessages();
      loadCategories();
    } else {
      setMessages([]);
      setCurrentStatus(null);
      setCurrentSite(null);
      setCurrentCategory('');
    }
  }, [ticket?.id, ticket?.status, ticket?.site, ticket?.category]);

  useEffect(() => {
    if (ticket) {
      loadCategories();
    }
  }, [ticket?.site]);

  const loadCategories = async (): Promise<TicketCategory[]> => {
    if (!ticket) return [];
    
    try {
      const data = await getTicketCategories(ticket.site);
      setCategories(data);
      return data;
    } catch (error) {
      console.error('Ошибка загрузки категорий:', error);
      return [];
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const loadMessages = async () => {
    if (!ticket) return;
    
    try {
      const data = await getTicketMessagesUseCase.execute(ticket.id);
      setMessages(data);
    } catch (error) {
      console.error('Ошибка загрузки сообщений:', error);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSend = async () => {
    if (!ticket) {
      console.warn('No ticket selected');
      return;
    }
    
    if (!inputValue.trim() && attachments.length === 0) {
      console.warn('No content or attachments to send');
      return;
    }
    
    if (sending) {
      console.warn('Already sending message');
      return;
    }

    const contentToSend = inputValue.trim();
    const attachmentsToSend = attachments.length > 0 ? attachments : undefined;

    try {
      setSending(true);
      console.log('Sending message:', { ticketId: ticket.id, content: contentToSend, hasAttachments: !!attachmentsToSend });
      
      await sendMessageUseCase.execute({
        ticketId: ticket.id,
        content: contentToSend,
        attachments: attachmentsToSend,
        isInternal: isInternal,
      });
      
      console.log('Message sent successfully');
      setInputValue('');
      setIsInternal(false);
      setAttachments([]);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      await loadMessages();
      onMessageSent?.();
    } catch (error: any) {
      console.error('Ошибка отправки сообщения:', error);
      const errorMessage = error?.response?.data?.error || error?.response?.data?.detail || error?.message || 'Не удалось отправить сообщение';
      alert(`Ошибка отправки: ${errorMessage}`);
    } finally {
      setSending(false);
      textareaRef.current?.focus();
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      setAttachments(prev => [...prev, ...files]);
    }
  };

  const handleRemoveAttachment = (index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const handleCopyTicketLink = async () => {
    if (!ticket) return;
    
    const ticketUrl = `${window.location.origin}/tickets/${ticket.id}`;
    try {
      await navigator.clipboard.writeText(ticketUrl);
      alert('Ссылка скопирована в буфер обмена');
    } catch (err) {
      console.error('Ошибка копирования:', err);
      const textArea = document.createElement('textarea');
      textArea.value = ticketUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      alert('Ссылка скопирована в буфер обмена');
    }
  };

  const handleCreateYouTrackIssue = async () => {
    if (!ticket) return;
    
    if (ticket.youtrackIssueUrl) {
      window.open(ticket.youtrackIssueUrl, '_blank');
      return;
    }
    
    if (!window.confirm('Создать тикет в YouTrack на основе этого обращения?')) {
      return;
    }
    
    try {
      setSending(true);
      const result = await createYouTrackIssue(ticket.id);
      if (result.success) {
        alert(`Тикет создан в YouTrack!\nID: ${result.issueId}`);
        onStatusChange?.();
      }
    } catch (error) {
      console.error('Ошибка создания тикета в YouTrack:', error);
      alert('Не удалось создать тикет в YouTrack. Проверьте консоль для подробностей.');
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !sending) {
      e.preventDefault();
      handleSend();
    }
  };

  const formatTime = (timestamp: string): string => {
    const date = new Date(timestamp);
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  const handleStatusChange = async (newStatus: TicketStatus) => {
    if (!ticket) return;

    try {
      setCurrentStatus(newStatus);
      await updateTicketStatusUseCase.execute({
        ticketId: ticket.id,
        status: newStatus,
      });
      onStatusChange?.(newStatus);
    } catch (error) {
      console.error('Ошибка изменения статуса:', error);
      setCurrentStatus(ticket.status);
      alert('Не удалось изменить статус');
    }
  };

  const handleSiteChange = async (newSite: TicketSite) => {
    if (!ticket) return;

    try {
      setCurrentSite(newSite);
      await updateTicketUseCase.execute({
        ticketId: ticket.id,
        data: { site: newSite },
      });
      const newCategories = await loadCategories();
      if (currentCategory && !newCategories.some(category => category.id === currentCategory)) {
        setCurrentCategory('');
        await updateTicketUseCase.execute({
          ticketId: ticket.id,
          data: { category: undefined },
        });
      }
      onStatusChange?.();
    } catch (error) {
      console.error('Ошибка изменения сайта:', error);
      setCurrentSite(ticket.site);
      alert('Не удалось изменить сайт');
    }
  };

  const handleCategoryChange = async (newCategory: string) => {
    if (!ticket) return;

    try {
      setCurrentCategory(newCategory);
      await updateTicketUseCase.execute({
        ticketId: ticket.id,
        data: { category: newCategory || undefined },
      });
      onStatusChange?.();
    } catch (error) {
      console.error('Ошибка изменения категории:', error);
      setCurrentCategory(ticket.category || '');
      alert('Не удалось изменить категорию');
    }
  };

  const handleTemplateInsert = (content: string) => {
    setInputValue(prev => prev ? `${prev}\n\n${content}` : content);
    textareaRef.current?.focus();
  };

  const getStatusLabel = (status: TicketStatus): string => {
    const labels: Record<TicketStatus, string> = {
      new: 'Новый',
      active: 'Открыто',
      waiting_info: 'Ожидаем ответ клиента',
      in_programming: 'В программирование',
      resolved: 'Решено',
      closed: 'Закрыто',
      spam: 'Спам',
      duplicate: 'Дубликат',
    };
    return labels[status] || status;
  };

  const getCategoryName = (categoryId?: string): string | undefined => {
    if (!categoryId) return undefined;
    const category = categories.find(cat => cat.id === categoryId);
    return category?.name;
  };


  if (!ticket) {
    return (
      <S.TicketChatContainer>
        <S.EmptyState>
          Выберите тикет для просмотра
        </S.EmptyState>
      </S.TicketChatContainer>
    );
  }

  return (
    <S.TicketChatContainer>
      <S.TicketChatHeader>
        <S.TicketChatTitleRow>
          <S.TicketChatTitle>
            {ticket.title}
            <S.TicketSiteBadge $site={ticket.site}>
              {ticket.site === 'hudle' ? 'Hudle' : ticket.site === 'unknown' ? 'Неизвестный' : 'Турнир'}
            </S.TicketSiteBadge>
            {ticket.ticketTypeName && (
              <S.TicketTypeBadge title="Тип обращения">
                {ticket.ticketTypeName}
              </S.TicketTypeBadge>
            )}
          </S.TicketChatTitle>
          <S.CopyLinkButton onClick={handleCopyTicketLink} title="Скопировать ссылку на тикет">
            <S3Icon iconName="Copy" width={18} height={18} />
          </S.CopyLinkButton>
        </S.TicketChatTitleRow>
        <S.TicketChatSubtitle>
          {(ticket.ticketTypeName ? `${ticket.ticketTypeName} / ` : '')}
          {getCategoryName(currentCategory || ticket.category) || 'Поддержка'} / {getStatusLabel(currentStatus || ticket.status)}
        </S.TicketChatSubtitle>
      </S.TicketChatHeader>

      <S.MessagesContainer>
        {messages.map(message => {
          const isFromClient = message.direction === 'incoming';
          const clientEmail = isFromClient && ticket.client.mainContact?.email;
          
          return (
            <S.MessageWrapper key={message.id} $direction={message.direction}>
              <S.MessageBubble 
                $direction={message.direction}
                $isInternal={message.isInternal || false}
                $themeMode={mode}
              >
                {message.isInternal && (
                  <S.InternalBadge $direction={message.direction} $themeMode={mode}>Внутреннее</S.InternalBadge>
                )}
                {isFromClient && clientEmail && (
                  <>
                    <S.ClientEmailLabel>
                      Почта:
                      <S.ClientEmail>{clientEmail}</S.ClientEmail>
                    </S.ClientEmailLabel>
                    <S.MessageLabel>Сообщение:</S.MessageLabel>
                  </>
                )}
                {!isFromClient && message.authorName && (
                  <S.AuthorName>{message.authorName}</S.AuthorName>
                )}
                <S.MessageContent $isInternal={message.isInternal} $themeMode={mode}>{message.content}</S.MessageContent>
                {message.attachments && message.attachments.length > 0 && (
                  <S.MessageAttachmentsList>
                    {message.attachments.map((attachment) => (
                      <S.MessageAttachmentItem key={attachment.id}>
                        {attachment.type === 'image' ? (
                          <S.AttachmentImage 
                            src={attachment.url} 
                            alt={attachment.name}
                            onClick={() => window.open(attachment.url, '_blank')}
                          />
                        ) : (
                          <S.AttachmentLink 
                            href={attachment.url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                          >
                            <S.AttachmentIcon>📎</S.AttachmentIcon>
                            <S.MessageAttachmentName>{attachment.name}</S.MessageAttachmentName>
                            {attachment.size && (
                              <S.AttachmentSize>
                                ({(attachment.size / 1024).toFixed(1)} KB)
                              </S.AttachmentSize>
                            )}
                          </S.AttachmentLink>
                        )}
                      </S.MessageAttachmentItem>
                    ))}
                  </S.MessageAttachmentsList>
                )}
                <S.MessageTimestamp $isOutgoing={message.direction === 'outgoing'}>
                  {formatTime(message.timestamp)}
                  {message.direction === 'outgoing' && message.authorName && (
                    <S.AuthorNameInTimestamp> • {message.authorName}</S.AuthorNameInTimestamp>
                  )}
                </S.MessageTimestamp>
              </S.MessageBubble>
            </S.MessageWrapper>
          );
        })}
        <S.MessagesEndRef ref={messagesEndRef} />
      </S.MessagesContainer>

      <S.MessageInputContainer>
        <S.StatusAndInputWrapper>
          <S.StatusSection>
            <S.StatusButton
              onClick={() => setIsStatusModalOpen(true)}
              $hasValue={!!(currentStatus || ticket.status)}
            >
              {getStatusLabel(currentStatus || ticket.status)}
            </S.StatusButton>
            <S.SiteButton
              onClick={() => setIsSiteModalOpen(true)}
              $hasValue={!!(currentSite || ticket.site)}
            >
              {ticket ? (currentSite || (ticket.site === 'hudle' ? 'Hudle' : ticket.site === 'unknown' ? 'Неизвестный' : 'Турнирный сайт')) : 'Hudle'}
            </S.SiteButton>
            <S.CategoryButton
              onClick={() => setIsCategoriesModalOpen(true)}
              $hasCategory={!!(currentCategory || ticket.category)}
            >
              {getCategoryName(currentCategory || ticket.category) || 'Категория'}
            </S.CategoryButton>
            <S.TemplatesButton onClick={() => setIsTemplatesModalOpen(true)}>
              Шаблоны
            </S.TemplatesButton>
            <S.CreateYTicketButton onClick={handleCreateYouTrackIssue} disabled={!ticket || sending}>
              {ticket?.youtrackIssueUrl ? 'Тикет YT' : 'Создать тикет YT'}
            </S.CreateYTicketButton>
          </S.StatusSection>
          <S.MessageInputWrapper
            as="form"
            onSubmit={(e) => {
              e.preventDefault();
              handleSend();
            }}
          >
            <S.MessageTextarea
              ref={textareaRef}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Напишите сообщение... (Enter для отправки, Shift+Enter для новой строки)"
              rows={3}
              disabled={sending}
            />
          </S.MessageInputWrapper>
        </S.StatusAndInputWrapper>
        <S.InputOptions>
          <S.InternalCheckbox>
            <input
              type="checkbox"
              checked={isInternal}
              onChange={(e) => setIsInternal(e.target.checked)}
              id="internal-message"
            />
            <label htmlFor="internal-message">Внутреннее сообщение (только для CRM)</label>
          </S.InternalCheckbox>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            style={{ display: 'none' }}
            onChange={handleFileSelect}
          />
          <S.AttachmentButton 
            title="Прикрепить файл"
            onClick={() => fileInputRef.current?.click()}
          >
            <S3Icon iconName={`AttachSolid`} />
          </S.AttachmentButton>
        </S.InputOptions>
        {attachments.length > 0 && (
          <S.AttachmentsList>
            {attachments.map((file, index) => (
              <S.AttachmentItem key={index}>
                <S.AttachmentName>{file.name}</S.AttachmentName>
                <S.RemoveAttachmentButton onClick={() => handleRemoveAttachment(index)}>
                  ×
                </S.RemoveAttachmentButton>
              </S.AttachmentItem>
            ))}
          </S.AttachmentsList>
        )}
      </S.MessageInputContainer>

      <CategoriesModal
        isOpen={isCategoriesModalOpen}
        onClose={() => setIsCategoriesModalOpen(false)}
        onSelect={handleCategoryChange}
        currentSite={ticket ? (currentSite || ticket.site) : 'hudle'}
        currentCategory={currentCategory || ticket.category}
      />

      <TemplatesModal
        isOpen={isTemplatesModalOpen}
        onClose={() => setIsTemplatesModalOpen(false)}
        onInsert={handleTemplateInsert}
      />

      <StatusModal
        isOpen={isStatusModalOpen}
        onClose={() => setIsStatusModalOpen(false)}
        onSelect={handleStatusChange}
        currentStatus={currentStatus || ticket.status}
        hasCategory={!!(currentCategory || ticket.category)}
      />

      <SiteModal
        isOpen={isSiteModalOpen}
        onClose={() => setIsSiteModalOpen(false)}
        onSelect={handleSiteChange}
        currentSite={currentSite || ticket.site}
      />
    </S.TicketChatContainer>
  );
};
